/* eslint-disable max-lines */
import { useContext, useCallback, useMemo } from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import {
  Article,
  ArticleResolution,
  ArticleType,
  Category,
  Label,
} from '@wix/answers-api';
import { helpCenterPageClick } from '@wix/bi-logger-customer-care-data/v2';
import { Box } from '@wix/design-system';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import { isProduction } from '@wix/wix-run-mode';
import cheerio from 'cheerio';
import {
  BI,
  EXPERIMENTS,
  LOCALES,
  MOCKED_ROADMAP_LABELS,
  PAGES,
} from '../../constants';
import { PageType } from '../../types';
import {
  getArticle,
  getArticleById,
  getLabels,
  getPageSeo,
} from '../../services/answers';
import {
  mapArticleTypeToItemType,
  mapPageTypeToSourceName,
} from '../../utils/bi';
import { ArticleContent } from '../../components/ArticleContent';
import { useBI } from '../../hooks/useBI';
import {
  conductExperimentByKey,
  withAppServerProps,
} from '../../utils/appServerProps';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { getRoadmapLabels, isRoadmapArticle } from '../../utils/roadmap';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { Context } from '../../context';
import { getCategoriesBreadcrumb } from '../../utils/categories';
import css from './Article.module.scss';
import { getWixDocsUrl } from '../../services/wixDocsRedirect';
import { shouldRedirectArticle } from '../../utils/redirectToWixDocs';
import { ArticleSideBar } from '../../components/ArticleSidebar';
import { WIX_EDITOR_X_CATEGORY_ID } from '../../components/CategoryPageWrapper';
import { getPersonalizationRelatedArticles } from '../../services/personalization';

export type Step = {
  id: string;
  text: string | null;
  level: string | null;
};

export type ArticlePageProps = {
  relatedArticles: Article[];
  article: Article;
  roadmapLabels: Label[];
  steps: Step[];
};

const ArticlePage: NextPage<ArticlePageProps> = ({
  article,
  relatedArticles,
  roadmapLabels,
  steps,
}: ArticlePageProps) => {
  const { locale } = useRouter();
  const { prePageUrl } = useHistoryContext();
  const { categoriesTree, isWixStudioUser } = useContext(Context);
  const { sendBIEvent } = useBI();
  const { t } = useTranslation();
  const router = useRouter();
  const { experiments } = useExperiments({ readOnly: true });

  const isFeatureRequestArticle = article.type === ArticleType.FEATURE_REQUEST;
  const showSideBar =
    steps.length > 0 ||
    relatedArticles.length > 0 ||
    (isWixStudioUser && isFeatureRequestArticle);

  const onBreadCrumbLinkClick = useCallback(
    async (category: Category, clickedItemOrder?: number): Promise<void> =>
      sendBIEvent(
        helpCenterPageClick({
          source_name: mapPageTypeToSourceName(PAGES.ARTICLE),
          item_id: article.id,
          item_type: mapArticleTypeToItemType(article.type),
          clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
          clicked_text: category.name,
          clicked_url: category.url,
          clicked_item_id: category.id,
          clicked_item_order: clickedItemOrder?.toString(),
          is_page_content_click: false,
        })
      ),
    [article.id, article.type]
  );

  const isRoadmap =
    article.resolution &&
    article.labels &&
    isRoadmapArticle(article.resolution, article.labels, roadmapLabels);

  const roadmapBreadcrumbs = {
    label: t('roadmap.breadcrumbs'),
    uri: `/roadmap`,
  };
  const isPrePageIsRoadmap = prePageUrl?.includes('/roadmap');

  const articleBreadcrumbItem = { id: article.id, label: article.title };
  const categoriesBreadcrumb = getCategoriesBreadcrumb(
    categoriesTree,
    article.categoryId,
    onBreadCrumbLinkClick
  );
  const breadcrumbItems = useMemo(
    () =>
      locale === LOCALES.EN && isRoadmap && isPrePageIsRoadmap
        ? [roadmapBreadcrumbs, articleBreadcrumbItem]
        : [...categoriesBreadcrumb, articleBreadcrumbItem],
    [categoriesTree, article]
  );

  const isEditorXSubCategory =
    categoriesBreadcrumb
      .map((item) => item.id)
      .includes(WIX_EDITOR_X_CATEGORY_ID) &&
    experiments.enabled(EXPERIMENTS.SPECS.EDITOR_X_NOTIFICATION);

  const onHomePageClick = useCallback(
    async (label: string) => {
      if (window) {
        await sendBIEvent(
          helpCenterPageClick({
            source_name: mapPageTypeToSourceName(PAGES.ARTICLE),
            item_id: article.id,
            item_type: mapArticleTypeToItemType(article.type),
            clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
            clicked_text: label,
            clicked_url: `${window.location.origin}/${locale}`,
            clicked_item_order: '1',
            clicked_item_id: undefined,
            is_page_content_click: false,
          })
        );
      }
    },
    [onBreadCrumbLinkClick, locale]
  );

  const onSelectStatus = async (resolution: ArticleResolution) => {
    await router.push(`/roadmap?resolutionType=${resolution}`);
  };

  return (
    <>
      <Breadcrumbs
        onHomePageClick={onHomePageClick}
        breadcrumbItems={breadcrumbItems}
        shallow={false}
      />

      <Box
        direction="horizontal"
        width="100%"
        align="center"
        className={css.articleWrapper}
      >
        <ArticleContent
          article={article}
          isRoadmapArticle={isRoadmap && locale === LOCALES.EN}
          readingTimeInMinutes={article.readingTimeInMinutes}
          onClickStatus={onSelectStatus}
          showEditorXNotification={isEditorXSubCategory}
        />
        {showSideBar && (
          <Box
            direction="vertical"
            position="relative"
            className={css.rightSide}
          >
            <ArticleSideBar
              article={article}
              steps={steps}
              isWixStudioUser={isWixStudioUser}
              relatedArticles={relatedArticles}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export const getServerSideProps = withAppServerProps(
  async ({ req, res, locale, query }) => {
    const { articleTitle } = query;

    const { article, relatedArticles } = await getArticle(
      req.aspects,
      articleTitle as string,
      locale
    );

    const allLabels = isProduction()
      ? await getLabels(req.aspects, res.locals.redisClient)
      : MOCKED_ROADMAP_LABELS;
    const roadmapLabels = getRoadmapLabels(allLabels as Label[]);

    if (shouldRedirectArticle(article.id)) {
      const isEnglish = article.locale === LOCALES.EN;

      const redirectArticle = !isEnglish
        ? await getArticleById(req.aspects, article.id as string, LOCALES.EN)
        : article;

      const wixDocsUrl = await getWixDocsUrl(
        res.locals.appContext,
        redirectArticle.url as string
      );

      if (wixDocsUrl) {
        return {
          redirect: {
            statusCode: 301,
            destination: wixDocsUrl,
          },
        };
      }
    }

    if (`/article/${articleTitle}` !== article.uri) {
      return {
        redirect: {
          statusCode: 301,
          destination: encodeURI(`/${locale}${article.uri}`),
        },
      };
    }

    const pageSeo = await getPageSeo(
      req.aspects,
      locale,
      PageType.Article,
      res.locals.redisClient
    );

    const getSteps = () => {
      if (!article.content) {
        return [];
      }
      const $ = cheerio.load(article.content);
      const newSteps = $(`[level="2"],[level="3"],[level="4"]`)
        .get()
        .filter((step) => step.attribs.id?.length > 0)
        .map((step) => ({
          id: step.attribs.id,
          text: $(step).text(),
          level: step.attribs.level,
        }));

      return newSteps.length > 1 ? newSteps : [];
    };

    let relatedArticlesToShow = relatedArticles;

    const isPersonalizedRelatedArticles = isProduction() && await conductExperimentByKey(
      res.locals.appContext,
      req.aspects,
      EXPERIMENTS.SPECS.PERSONALIZED_RELATED_ARTICLES
    ) || { value: 'false' };

    const { value: isArticleFeedbackFeb24VersionEnabled } = (isProduction() &&
    (await conductExperimentByKey(
      res.locals.appContext,
      req.aspects,
      EXPERIMENTS.SPECS.ARTICLE_FEEDBACK_FEB24_VERSION
    ))) || { value: 'false' };

    if (isPersonalizedRelatedArticles.value === 'true') {
      relatedArticlesToShow = await getPersonalizationRelatedArticles(
        req.aspects,
        res.locals.appContext,
        article.id,
        locale,
        relatedArticles
      );
    }

    return {
      props: {
        relatedArticles: relatedArticlesToShow,
        article,
        pageData: {
          article: {
            ...article,
            withRelatedArticles:
              !!relatedArticlesToShow && relatedArticlesToShow.length,
          },
          roadmapData: { roadmapLabels },
          personalizedContent: {
            section: BI.PERSONALIZED_CONTENT_SECTIONS.RELATED_CONTENT,
            content: (relatedArticlesToShow as Article[]).map((e) => e.id),
          },
        },
        pageType: PAGES.ARTICLE,
        itemId: article.id,
        itemType: mapArticleTypeToItemType(article?.type),
        pageSeo,
        roadmapLabels,
        steps: getSteps(),
      },
      experimentsPageProps: {
        [EXPERIMENTS.SPECS.ARTICLE_FEEDBACK_FEB24_VERSION]:
          isArticleFeedbackFeb24VersionEnabled as string
      },
    };
  }
);

export default ArticlePage;
